import {
  Checkbox,
  Flex,
  IconArrowOpenDownLine,
  IconArrowOpenUpLine,
  IconButton,
  IconTrashLine,
  RadioInput,
  RadioInputGroup,
  Text,
  TextArea,
  TextInput,
} from "@instructure/ui";
import { t } from "i18next";
import { memo, useState } from "react";
import {
  CANVAS,
  EMBEDDED,
  EXTERNAL,
  QUALITATIVE,
} from "../Assessment/Plan/utils";
import CustomRTE, { EMPTY_RTE } from "../CustomTextArea/CustomRTE";
import { SINGLE, Selector } from "../Selectors/Selector";

const MeasureCard = memo(
  ({ index, lastMeasure, measure, setSections, sectionIndex }) => {
    const [assignmentDescError, setAssignmentDescError] = useState([]);

    const handleTypeSelect = (type) => {
      let obj = {};
      if (type === CANVAS || type === EXTERNAL) {
        obj = {
          index: measure.index,
          type,
          collecting_evidence: false,
          description: "",
        };
      } else if (type === QUALITATIVE) {
        obj = {
          index: measure.index,
          type,
          name: "",
          upload_evidence: false,
          description: EMPTY_RTE,
        };
      } else if (type === EMBEDDED) {
        obj = { index: measure.index, type, name: "", description: "" };
      }

      setSections((prev) =>
        prev.map((section, i) =>
          i === sectionIndex
            ? {
                ...section,
                measures: section.measures.map((m, j) =>
                  j === index ? obj : m
                ),
              }
            : section
        )
      );
    };

    const moveMeasureUp = () => {
      setSections((prev) =>
        prev.map((section, i) =>
          i === sectionIndex
            ? {
                ...section,
                measures: section.measures.map((m, j) =>
                  j === index
                    ? section.measures[index - 1]
                    : j === index - 1
                      ? section.measures[index]
                      : m
                ),
              }
            : section
        )
      );
    };

    const moveMeasureDown = () => {
      setSections((prev) =>
        prev.map((section, i) =>
          i === sectionIndex
            ? {
                ...section,
                measures: section.measures.map((m, j) =>
                  j === index
                    ? section.measures[index + 1]
                    : j === index + 1
                      ? section.measures[index]
                      : m
                ),
              }
            : section
        )
      );
    };

    const handleDeleteMeasure = () => {
      setSections((prev) =>
        prev.map((section, i) =>
          i === sectionIndex
            ? {
                ...section,
                measures: section.measures.filter((_, j) => j !== index),
              }
            : section
        )
      );
    };

    const handleNameChange = (e) => {
      setSections((prev) =>
        prev.map((section, i) =>
          i === sectionIndex
            ? {
                ...section,
                measures: section.measures.map((m, j) =>
                  j === index ? { ...m, name: e.target.value } : m
                ),
              }
            : section
        )
      );
    };

    const handleUploadEvidenceChange = (e) => {
      setSections((prev) =>
        prev.map((section, i) =>
          i === sectionIndex
            ? {
                ...section,
                measures: section.measures.map((m, j) =>
                  j === index
                    ? { ...m, upload_evidence: e.target.value === "yes" }
                    : m
                ),
              }
            : section
        )
      );
    };

    const handleCollectSubmissionsChange = (e) => {
      setSections((prev) =>
        prev.map((section, i) =>
          i === sectionIndex
            ? {
                ...section,
                measures: section.measures.map((m, j) =>
                  j === index
                    ? { ...m, collecting_evidence: e.target.checked }
                    : m
                ),
              }
            : section
        )
      );
    };

    const handleDescriptionChange = (e) => {
      setSections((prev) =>
        prev.map((section, i) =>
          i === sectionIndex
            ? {
                ...section,
                measures: section.measures.map((m, j) =>
                  j === index ? { ...m, description: e.target.value } : m
                ),
              }
            : section
        )
      );
    };

    const handleInstructionsChange = (value) => {
      // RTE doesn't have a target property, so we add value directly
      setSections((prev) =>
        prev.map((section, i) =>
          i === sectionIndex
            ? {
                ...section,
                measures: section.measures.map((m, j) =>
                  j === index ? { ...m, description: value } : m
                ),
              }
            : section
        )
      );
    };

    return (
      <Flex wrap="wrap" gap="small">
        <Flex.Item size="50%">
          <Selector
            type={SINGLE}
            id={`assessment-type-select-section-${sectionIndex}-measure-${index}`}
            label={t("measures.typeOf")}
            options={[
              { id: CANVAS, label: t("assessment.canvasAssessment") },
              { id: EXTERNAL, label: t("assessment.externalAssessment") },
              { id: QUALITATIVE, label: t("assessment.qualitativeMeasure") },
              { id: EMBEDDED, label: t("embeddedDashboard.embeddedDashboard") },
            ]}
            selectHandler={handleTypeSelect}
            selectedItemId={measure.type}
            size="small"
          />
        </Flex.Item>
        <Flex.Item align="start" shouldGrow textAlign="end">
          <IconButton
            renderIcon={IconArrowOpenUpLine}
            screenReaderLabel={t("measures.moveMeasureUp")}
            size="small"
            onClick={moveMeasureUp}
            disabled={index === 0}
          />
          <IconButton
            renderIcon={IconArrowOpenDownLine}
            screenReaderLabel={t("measures.moveMeasureDown")}
            size="small"
            onClick={moveMeasureDown}
            margin="0 0 0 small"
            disabled={!!lastMeasure}
          />
          <IconButton
            renderIcon={IconTrashLine}
            screenReaderLabel={t("measures.delete")}
            size="small"
            onClick={handleDeleteMeasure}
            margin="0 0 0 small"
          />
        </Flex.Item>
        {measure.type === QUALITATIVE || measure.type === EMBEDDED ? (
          <>
            <Flex.Item size="50%">
              <TextInput
                id={`measure-name-section-${sectionIndex}-measure-${index}`}
                renderLabel={
                  <Text size="small">
                    {measure.type === QUALITATIVE
                      ? t("measures.name")
                      : t("assessment.name")}
                  </Text>
                }
                size="small"
                defaultValue={measure.name}
                onBlur={handleNameChange}
              />
            </Flex.Item>
            {measure.type === EMBEDDED && (
              <Flex.Item size="100%">
                <TextArea
                  id={`embedded-dashboard-desc-section-${sectionIndex}-measure-${index}`}
                  label={
                    <Text size="small">
                      {t("embeddedDashboard.description")}
                    </Text>
                  }
                  defaultValue={measure.description}
                  onBlur={handleDescriptionChange}
                />
              </Flex.Item>
            )}
            {measure.type === QUALITATIVE && (
              <>
                <Flex.Item>
                  <RadioInputGroup
                    name={`qm-evidence-section-${sectionIndex}-measure-${index}`}
                    value={measure.upload_evidence ? "yes" : "no"}
                    description={
                      <Text size="small">{t("measures.uploadEvidence")}?</Text>
                    }
                    layout="columns"
                    onChange={handleUploadEvidenceChange}
                  >
                    <RadioInput value="yes" label={t("dialogYesNo.yes")} />
                    <RadioInput value="no" label={t("dialogYesNo.no")} />
                  </RadioInputGroup>
                </Flex.Item>
                <Flex.Item size="100%">
                  <CustomRTE
                    label={
                      <Text weight="bold" size="small">
                        {t("assessment.instructions")}{" "}
                        <Text size="small" color="secondary" fontStyle="italic">
                          ({t("form.optional")})
                        </Text>
                      </Text>
                    }
                    defaultValue={measure.description}
                    onChange={handleInstructionsChange}
                  />
                </Flex.Item>
              </>
            )}
          </>
        ) : (
          <>
            <Flex.Item size="100%">
              <Checkbox
                id={`collect-submissions-section-${sectionIndex}-measure-${index}`}
                label={t("assessment.collectSubmissions")}
                checked={measure.collecting_evidence}
                variant="toggle"
                size="small"
                onChange={handleCollectSubmissionsChange}
              />
            </Flex.Item>
            <Flex.Item size="100%">
              <TextArea
                id={`assessment-assignment-desc-section-${sectionIndex}-measure-${index}`}
                label={
                  <Text size="small">
                    {t("assessment.assignmentDesc")}{" "}
                    <Text size="small" color="secondary" fontStyle="italic">
                      ({t("form.optional")})
                    </Text>
                  </Text>
                }
                defaultValue={measure.description}
                onBlur={(e) => {
                  handleDescriptionChange(e);
                  if (e.target.value.length > 255) {
                    setAssignmentDescError([
                      { text: t("assessment.descLimit"), type: "error" },
                    ]);
                  } else {
                    setAssignmentDescError([]);
                  }
                }}
                messages={assignmentDescError}
              />
            </Flex.Item>
          </>
        )}
      </Flex>
    );
  }
);

export default MeasureCard;
